import React, {useEffect, useState} from "react";
import useIsMounted from "../../../hooks/use-is-mounted";
import {DialogContent, DialogTitle, Divider, MenuItem} from "@mui/material";
import {Col} from "reactstrap";
import Dialog from "../../base/dialog";
import Button from "../../base/button/button";
import Chips, {ChipsContentType, ChipsIconPosition, ChipsTypes} from "../../base/chips/chips";
import Api from "../../../../core/services/api/api";
import TagChipsContent from "../../app-specific/chips-contents/tag-chips-content";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {makeValidate} from "mui-rff";
import Form from "../../base/form";
import useIndustry from "../../../hooks/use-industry";
import LoadingLottieAnimation from "../../app-specific/lottie-animations/loading";
import {MuiRFF, Yup} from '../../form'


const formNames = {
    keyword: "keyword",
    industry: "industry",
}

const schema = Yup.object().shape({
    [formNames.keyword]: Yup.string().trim().nullable().required(ValidateMessages.required),
    [formNames.industry]: Yup.mixed().nullable().required(ValidateMessages.required),
});

const validate = makeValidate(schema);
const required = makeValidate(schema);

const CreateNewTagsDialog = ({open, close, onSuccess, initialValues: initialValuesProps, limitToOne}) => {
    const {industries, cachedIndustry, fetchedIndustry, fetchIndustry, loadingIndustries, hasNoIndustries} = useIndustry({noCategories: true});
    const [addedTags, setAddedTags] = useState([]);
    const isMounted = useIsMounted();

    const initialValues = React.useMemo(() => ({
        [formNames.industry]: fetchedIndustry ?? cachedIndustry ?? null,
        [formNames.keyword]: initialValuesProps?.keyword ?? '',
    }), [fetchedIndustry, cachedIndustry, initialValuesProps?.keyword])

    /**
     * Automatically fetches the industry if there is an industryId in the initialValues, and it's not yet fetched or selected
     */
    useEffect(() => {
        if (!cachedIndustry?.id || !initialValuesProps?.industryId)
            return;
        void fetchIndustry({industryId: initialValuesProps?.industryId ?? cachedIndustry.id});
    }, [cachedIndustry?.id, fetchIndustry, initialValuesProps?.industryId]);

    /**
     * Automatically fetches the industry if there is only one industry available and it's not already fetched or selected
     */
    useEffect(() => {
        if (industries?.length === 1) {
            const industry = industries[0];
            void fetchIndustry({industryId: industry.id});
        }
    }, [fetchIndustry, industries]);

    /**
     * With each change in the open and close state of the tab clears the added tags
     */
    useEffect(() => {
        setAddedTags([]);
    }, [open])

    /**
     * Removes the selected tag from the system.
     *
     * * if the response of the api call is successful, removes the tag form the addedTags state list as well.
     * @param tag
     */
    const removeSelectedTag = async (tag) => {
        const forApi = {
            ids: [tag.id],
        }
        setAddedTags(prevState => prevState.map(e => e.id === tag.id
            ? {...tag, loading: true}
            : e
        ));
        const response = await Api.deleteTagsFromSystem(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setAddedTags(prevState => prevState.filter(e => e.id !== tag.id));
        } else {
            setAddedTags(prevState => prevState.map(e => e.id === tag.id
                ? {...tag, loading: false}
                : e
            ));
        }
    }

    /**
     * Creates a new tag from the given form values in the server.
     *
     * * if the response of the api call is successful, then resets the form input's vlaue.
     * @param values
     * @param form
     */
    const createNewTag = async (values, form) => {
        const forApi = {
            name: values[formNames.keyword],
            industryId: values[formNames.industry]?.id,
        }
        const response = await Api.createNewTag(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            if (limitToOne) {
                return commitChanges([response?.data ?? {}]);
            }
            document.activeElement?.blur();
            setAddedTags(prevState => [...prevState, {...(response?.data ?? {}), loading: false,}])
            setTimeout(() => {
                form.change(formNames.keyword, '');
                form.change(formNames.industry, null);
                form.resetFieldState(formNames.keyword);
                if (industries?.length > 1) {
                    form.resetFieldState(formNames.industry);
                }
            }, 100)
        }
    }

    /**
     * Invokes the onSuccess callback with the added tags list and then closes the dialog.
     */
    const commitChanges = (addedTags) => {
        if (addedTags?.length) {
            onSuccess(limitToOne ? addedTags[0] : addedTags);
        }
        close();
    }

    return (
        <>
            <Dialog
                open={open}
                setOpen={() => commitChanges(addedTags)}
                maxWidth={'sm'}
                className={'bound-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        Add New Tags
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={'px-2'}>
                    {
                        hasNoIndustries &&
                        <p className={'text-sm text-color-3 font-weight-semi-bold text-center my-5'}>
                            There are no industries to create a product for. Please contact the system administrator.
                        </p>
                    }
                    {
                        !hasNoIndustries && loadingIndustries &&
                        <LoadingLottieAnimation width={'80%'}/>
                    }
                    {
                        !hasNoIndustries && !loadingIndustries &&
                        <>
                            <Form
                                className='d-flex flex-wrap w-100 align-items-start mt-3'
                                onSubmit={createNewTag}
                                id={'form-id'}
                                validate={validate}
                                initialValues={initialValues}
                                render={({submitting}) => {
                                    return (
                                        <>
                                            <Col xs={12} className={'mb-4 px-0'}>
                                                <div className={'d-flex flex-wrap w-100 align-items-center justify-content-center mt-4 w'}>
                                                    {
                                                        industries.length > 1 &&
                                                        <Col xs={12} className={'mb-3'}>
                                                            <MuiRFF.TextField
                                                                fullWidth
                                                                label={'Industry'}
                                                                name={formNames.industry}
                                                                required={required[formNames.industry]}
                                                                select
                                                                SelectProps={{
                                                                    renderValue: (industry) => industry?.title ?? '',
                                                                }}
                                                            >
                                                                {
                                                                    industries.map(industry => (
                                                                        <MenuItem key={industry.id} value={industry}>
                                                                            {industry.title}
                                                                        </MenuItem>
                                                                    ))
                                                                }
                                                            </MuiRFF.TextField>
                                                        </Col>
                                                    }
                                                    <Col xs={12} className={'mb-3'}>
                                                        <MuiRFF.TextField
                                                            fullWidth
                                                            name={formNames.keyword}
                                                            placeholder={'Type...'}
                                                            label={"Tag Title"}
                                                            required={required[formNames.keyword]}
                                                        />
                                                    </Col>
                                                    <Col xs={12} className={'d-flex align-items-center justify-content-center'}>
                                                        <Button
                                                            type={'submit'}
                                                            name={'search'}
                                                            disabled={submitting}
                                                            variant={'contained'}
                                                            color={'primary'}
                                                            size={'large'}
                                                        >
                                                            {
                                                                submitting
                                                                    ? "Creating..."
                                                                    : "Create"
                                                            }
                                                        </Button>
                                                    </Col>
                                                </div>
                                            </Col>
                                        </>
                                    )
                                }}
                            />
                            {
                                !limitToOne &&
                                <div className={'w-100 d-flex flex-wrap mb-4 px-3'}>
                                    {
                                        addedTags.map(tag => (
                                            <Chips
                                                key={tag.id}
                                                className={'mr-2 mb-2'}
                                                contentType={ChipsContentType.element}
                                                type={tag.loading ? ChipsTypes.loading : ChipsTypes.remove}
                                                iconPosition={ChipsIconPosition.start}
                                                disabled={tag.loading}
                                                onIconClick={() => removeSelectedTag(tag)}
                                                content={<TagChipsContent tag={tag} useIcon/>}
                                            />
                                        ))
                                    }
                                </div>
                            }
                        </>
                    }
                </DialogContent>
            </Dialog>
        </>
    );
}

export default CreateNewTagsDialog;
